import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch, query, limit } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import Navigacija from './components/Navigacija';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Pocetna from './pages/Pocetna';
import Utakmice from './pages/Utakmice';
import DetaljiUtakmice from './pages/DetaljiUtakmice';
import Treninzi from './pages/Treninzi';
import Igraci from './pages/Igraci';
import Profil from './pages/Profil';
import Ranglista from './pages/Ranglista';
import Izvjestaji from './pages/Izvjestaji';
import Raspored from './pages/Raspored';
import Turniri from './pages/Turniri';
import Logi from './pages/Logi';
import { logGresku } from './utils/logger';

function generisiSezone() {
  const godina = new Date().getFullYear();
  const sezone = [];
  for (let g = 2023; g <= godina + 1; g++) {
    sezone.push(`${g}/${g + 1}`);
  }
  return sezone;
}
const SEZONE = generisiSezone();

function getAktivnaSezona() {
  const datum = new Date();
  const godina = datum.getFullYear();
  const mjesec = datum.getMonth() + 1;
  if (mjesec >= 7) return godina + '/' + (godina + 1);
  return (godina - 1) + '/' + godina;
}

function App() {
  const [korisnik, setKorisnik] = useState(null);
  const [aktivna, setAktivna] = useState({ strana: 'pocetna' });
  const [globalnaSezona, setGlobalnaSezona] = useState(getAktivnaSezona());
  const [igraci, setIgraci] = useState([]);
  const [utakmice, setUtakmice] = useState([]);
  const [treninzi, setTreninzi] = useState([]);
  const [raspored, setRaspored] = useState([]);
  const [turniri, setTurniri] = useState([]);
  const [logi, setLogi] = useState([]);
  const [planoviTreninga, setPlanoviTreninga] = useState([]);
  const [ucitavanje, setUcitavanje] = useState(true);
  const korisnikRef = useRef(null);
  const aktivnaRef = useRef({ strana: 'pocetna' });

  // Prati korisnika i aktivnu stranu u ref-ovima (za globalne error handlere)
  useEffect(() => { korisnikRef.current = korisnik; }, [korisnik]);
  useEffect(() => { aktivnaRef.current = aktivna; }, [aktivna]);

  // Globalni error handleri
  useEffect(() => {
    const onError = (event) => {
      logGresku({
        tip: 'js_greska',
        poruka: event.message || String(event.error),
        stack: event.error?.stack || '',
        strana: aktivnaRef.current?.strana || '?',
        korisnik: korisnikRef.current?.ime || 'nepoznat',
      });
    };
    const onUnhandled = (event) => {
      logGresku({
        tip: 'promise_greska',
        poruka: String(event.reason?.message || event.reason || 'Unhandled promise rejection'),
        stack: event.reason?.stack || '',
        strana: aktivnaRef.current?.strana || '?',
        korisnik: korisnikRef.current?.ime || 'nepoznat',
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandled);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandled);
    };
  }, []);

  useEffect(() => {
    if (korisnik) ucitajSve();
    else setUcitavanje(false);
  }, [korisnik]);

  const ucitajSve = async () => {
    setUcitavanje(true);
    try {
      const [igraciSnap, utakmiceSnap, treninziSnap, rasporedSnap, turniriSnap] = await Promise.all([
        getDocs(collection(db, 'igraci')),
        getDocs(collection(db, 'utakmice')),
        getDocs(collection(db, 'treninzi')),
        getDocs(collection(db, 'raspored')),
        getDocs(collection(db, 'turniri')),
      ]);
      const utakmiceData = utakmiceSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setIgraci(igraciSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setUtakmice(utakmiceData);
      setTreninzi(treninziSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRaspored(rasporedSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTurniri(turniriSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Automatski postavi aktivnu sezonu: uzima najnoviju od (datuma, podataka)
      // — nikad ne downgraduje na stariju ako datum već pokazuje noviju
      const treninziData = treninziSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const sveSezone = [
        ...utakmiceData.map(u => u.sezona),
        ...treninziData.map(t => t.sezona),
      ].filter(Boolean);
      const sezoneUPodacima = [...new Set(sveSezone)].sort();
      if (sezoneUPodacima.length > 0) {
        const najnovijaUPodacima = sezoneUPodacima[sezoneUPodacima.length - 1];
        // Uzmi max: ne downgraduj ako datum daje noviju sezonu
        setGlobalnaSezona(prev => najnovijaUPodacima > prev ? najnovijaUPodacima : prev);
      }
    } catch (err) {
      console.error('Greska pri ucitavanju podataka:', err);
    }

    // Planovi treninga — odvojeno da ne blokira glavne podatke
    try {
      const planoviSnap = await getDocs(collection(db, 'planoviTreninga'));
      setPlanoviTreninga(planoviSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn('Planovi treninga nisu ucitani:', err.message);
    }

    // Logi se ucitava odvojeno da ne blokira glavne podatke ako nema pristupa
    try {
      const logiSnap = await getDocs(query(collection(db, 'logi'), limit(200)));
      setLogi(logiSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.warn('Logi nisu ucitani (moguce Firestore pravila):', err.message);
    }

    setUcitavanje(false);
  };

  // Browser history podrška — prati navigaciju
  const navigiraj = (nova) => {
    window.history.pushState(nova, '');
    setAktivna(nova);
  };

  useEffect(() => {
    const handlePopState = (e) => {
      if (e.state?.strana) {
        setAktivna(e.state);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLogin = (k) => {
    setKorisnik(k);
    const pocetna = { strana: 'pocetna' };
    window.history.replaceState(pocetna, '');
    setAktivna(pocetna);
  };

  const handleLogout = async () => {
    if (korisnik?.tip === 'admin') await signOut(auth);
    setKorisnik(null);
    setIgraci([]);
    setUtakmice([]);
    setTreninzi([]);
  };

  const dodajIgraca = async (igrac) => {
    const ref = await addDoc(collection(db, 'igraci'), { ...igrac, kreiran: new Date().toISOString() });
    setIgraci(prev => [...prev, { id: ref.id, ...igrac }]);
  };

  const updateIgrac = async (id, podaci) => {
    await updateDoc(doc(db, 'igraci', id), podaci);
    setIgraci(prev => prev.map(i => i.id === id ? { ...i, ...podaci } : i));
  };

  const obrisiIgraca = async (id) => {
    await deleteDoc(doc(db, 'igraci', id));
    setIgraci(prev => prev.filter(i => i.id !== id));
  };

  const sacuvajUtakmicu = async (utakmica) => {
    const ref = await addDoc(collection(db, 'utakmice'), { ...utakmica, kreiran: new Date().toISOString() });
    setUtakmice(prev => [...prev, { id: ref.id, ...utakmica }]);
  };

  const updateUtakmicu = async (id, podaci) => {
    await updateDoc(doc(db, 'utakmice', id), podaci);
    setUtakmice(prev => prev.map(u => u.id === id ? { ...u, ...podaci } : u));
  };

  const obrisiUtakmicu = async (id) => {
    await deleteDoc(doc(db, 'utakmice', id));
    setUtakmice(prev => prev.filter(u => u.id !== id));
  };

  const sacuvajTrening = async (trening) => {
    const ref = await addDoc(collection(db, 'treninzi'), { ...trening, kreiran: new Date().toISOString() });
    setTreninzi(prev => [...prev, { id: ref.id, ...trening }]);
  };

  const updateTrening = async (id, podaci) => {
    await updateDoc(doc(db, 'treninzi', id), podaci);
    setTreninzi(prev => prev.map(t => t.id === id ? { ...t, ...podaci } : t));
  };

  const obrisiTrening = async (id) => {
    await deleteDoc(doc(db, 'treninzi', id));
    setTreninzi(prev => prev.filter(t => t.id !== id));
  };

  const dodajRaspored = async (unos) => {
    const ref = await addDoc(collection(db, 'raspored'), { ...unos, kreiran: new Date().toISOString() });
    setRaspored(prev => [...prev, { id: ref.id, ...unos }]);
  };

  const updateRaspored = async (id, podaci) => {
    await updateDoc(doc(db, 'raspored', id), podaci);
    setRaspored(prev => prev.map(r => r.id === id ? { ...r, ...podaci } : r));
  };

  const obrisiRaspored = async (id) => {
    await deleteDoc(doc(db, 'raspored', id));
    setRaspored(prev => prev.filter(r => r.id !== id));
  };

  const dodajTurnir = async (turnir) => {
    const ref = await addDoc(collection(db, 'turniri'), { ...turnir, kreiran: new Date().toISOString() });
    setTurniri(prev => [...prev, { id: ref.id, ...turnir }]);
  };

  const updateTurnir = async (id, podaci) => {
    await updateDoc(doc(db, 'turniri', id), podaci);
    setTurniri(prev => prev.map(t => t.id === id ? { ...t, ...podaci } : t));
  };

  const obrisiTurnir = async (id) => {
    await deleteDoc(doc(db, 'turniri', id));
    setTurniri(prev => prev.filter(t => t.id !== id));
  };

  const sacuvajPlanTreninga = async (plan) => {
    const ref = await addDoc(collection(db, 'planoviTreninga'), { ...plan, kreiran: new Date().toISOString() });
    setPlanoviTreninga(prev => [...prev, { id: ref.id, ...plan }]);
  };
  const azurirajPlanTreninga = async (id, plan) => {
    await updateDoc(doc(db, 'planoviTreninga', id), { ...plan, azuriran: new Date().toISOString() });
    setPlanoviTreninga(prev => prev.map(p => p.id === id ? { ...p, ...plan, azuriran: new Date().toISOString() } : p));
  };
  const obrisiPlanTreninga = async (id) => {
    await deleteDoc(doc(db, 'planoviTreninga', id));
    setPlanoviTreninga(prev => prev.filter(p => p.id !== id));
  };

  const obrisiLog = async (id) => {
    await deleteDoc(doc(db, 'logi', id));
    setLogi(prev => prev.filter(l => l.id !== id));
  };

  const obrisiSveLoge = async () => {
    const CHUNK = 490;
    for (let i = 0; i < logi.length; i += CHUNK) {
      const batch = writeBatch(db);
      logi.slice(i, i + CHUNK).forEach(l => batch.delete(doc(db, 'logi', l.id)));
      await batch.commit();
    }
    setLogi([]);
  };

  if (!korisnik) return <Login onLogin={handleLogin} />;

  if (ucitavanje) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f0f1a' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px' }}>🎳</div>
        <p style={{ color: '#666688', marginTop: '12px' }}>Ucitavanje...</p>
      </div>
    </div>
  );

  const jeAdmin = korisnik.tip === 'admin';
  const jeGost = korisnik.tip === 'gost';
  const igracKorisnik = korisnik?.igracId ? igraci.find(i => i.id === korisnik.igracId) : null;
  const jeTrener = igracKorisnik?.jeTrener === true;
  const props = { igraci, utakmice, treninzi, raspored, turniri, setAktivna: navigiraj, globalnaSezona };

  return (
    <div style={{ fontFamily: 'Arial', maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      <Navigacija
        aktivna={aktivna}
        setAktivna={navigiraj}
        korisnik={korisnik}
        onLogout={handleLogout}
        jeAdmin={jeAdmin}
        jeGost={jeGost}
        globalnaSezona={globalnaSezona}
        setGlobalnaSezona={setGlobalnaSezona}
        sezone={SEZONE}
        brojaGresaka={logi.length}
      />

      {aktivna.strana === 'pocetna' && (
        <Pocetna {...props} jeAdmin={jeAdmin} jeGost={jeGost} />
      )}

      {aktivna.strana === 'utakmice' && (
        <Utakmice {...props}
          otvoriUnos={aktivna.otvoriUnos}
          predUnos={aktivna.predUnos}
          sacuvajUtakmicu={sacuvajUtakmicu}
          obrisiUtakmicu={obrisiUtakmicu}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'detaljiUtakmice' && (
        <DetaljiUtakmice {...props}
          utakmica={utakmice.find(u => u.id === aktivna.utakmicaId)}
          updateUtakmicu={updateUtakmicu}
          obrisiUtakmicu={obrisiUtakmicu}
          jeAdmin={jeAdmin}
        />
      )}

      {aktivna.strana === 'treninzi' && (
        <Treninzi {...props}
          otvoriUnos={aktivna.otvoriUnos}
          sacuvajTrening={sacuvajTrening}
          updateTrening={updateTrening}
          obrisiTrening={obrisiTrening}
          planoviTreninga={planoviTreninga}
          sacuvajPlan={sacuvajPlanTreninga}
          azurirajPlan={azurirajPlanTreninga}
          obrisiPlan={obrisiPlanTreninga}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
          jeTrener={jeTrener}
          korisnik={korisnik}
        />
      )}

      {aktivna.strana === 'igraci' && (
        <Igraci {...props}
          setIgraci={setIgraci}
          dodajIgraca={dodajIgraca}
          updateIgrac={updateIgrac}
          obrisiIgraca={obrisiIgraca}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'profil' && (
        <Profil {...props}
          igracId={aktivna.igracId}
          povratakNa={aktivna.povratakNa}
          pocetniTab={aktivna.pocetniTab}
          updateIgrac={updateIgrac}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'ranglista' && (
        <Ranglista {...props}
          inicialniKlub={aktivna.klub}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'raspored' && (
        <Raspored {...props}
          dodajRaspored={dodajRaspored}
          updateRaspored={updateRaspored}
          obrisiRaspored={obrisiRaspored}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'turniri' && (
        <Turniri {...props}
          dodajTurnir={dodajTurnir}
          updateTurnir={updateTurnir}
          obrisiTurnir={obrisiTurnir}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'izvjestaji' && (
        <Izvjestaji {...props}
          jeAdmin={jeAdmin}
          jeGost={jeGost}
        />
      )}

      {aktivna.strana === 'logi' && jeAdmin && (
        <Logi
          logi={logi}
          obrisiLog={obrisiLog}
          obrisiSveLoge={obrisiSveLoge}
        />
      )}

    </div>
  );
}

function AppSaErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppSaErrorBoundary;
